import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { SourceIcon } from "../shared/SourceIcon";
import { COLORS, FONTS, SPRING } from "./constants";

const SOURCES = [
  { name: "GitHub", icon: "\u2699\uFE0F", angle: 0 },
  { name: "Jira", icon: "\uD83D\uDCCB", angle: 60 },
  { name: "Confluence", icon: "\uD83D\uDCC4", angle: 120 },
  { name: "Slack", icon: "\uD83D\uDCAC", angle: 180 },
  { name: "Google Drive", icon: "\uD83D\uDCC1", angle: 240 },
  { name: "Upload", icon: "\uD83D\uDCCE", angle: 300 },
] as const;

const STATS_ROWS = [
  { label: "Supported file types", value: "PDF, DOCX, XLSX, CSV, MD, TXT" },
  { label: "Code languages", value: "13 (TypeScript, Python, Go, Rust, C++...)" },
  { label: "Documents per source", value: "670+" },
  { label: "Knowledge chunks", value: "13,000+" },
  { label: "Full sync time", value: "~14 minutes" },
  { label: "Incremental sync", value: "SHA-256 dedup \u2014 only changed files" },
] as const;

const CX = 960;
const CY = 450;
const RADIUS = 280;

export const Scene04Sources: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // --- Phase A: Source Icons (0-450) ---
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Hub glow
  const hubScale = spring({
    frame: frame - 20,
    fps,
    config: SPRING.bouncy,
  });

  // Phase A hold then dim for Phase B
  const iconsOpacity = interpolate(frame, [440, 470], [1, 0.3], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase B: Stats Table (450-900) ---
  const statsCardOpacity = interpolate(frame, [450, 490], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const statsCardY = interpolate(frame, [450, 490], [30, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Fade out 860-900
  const fadeOut = interpolate(frame, [860, 900], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill>
      <GridBackground />

      <AbsoluteFill style={{ opacity: fadeOut }}>
        {/* Title */}
        <div
          style={{
            position: "absolute",
            top: 80,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: FONTS.heading,
            fontSize: 40,
            fontWeight: 700,
            color: COLORS.textPrimary,
            opacity: titleOpacity,
          }}
        >
          Connect the tools your team already uses
        </div>

        {/* Source icons in circle */}
        <div style={{ opacity: iconsOpacity }}>
          {SOURCES.map((src, i) => {
            const rad = (src.angle * Math.PI) / 180;
            const tx = CX + RADIUS * Math.cos(rad);
            const ty = CY + RADIUS * Math.sin(rad);
            return (
              <SourceIcon
                key={src.name}
                name={src.name}
                icon={src.icon}
                x={tx}
                y={ty}
                delay={20 + i * 20}
                size={80}
              />
            );
          })}

          {/* Dashed lines from each source to hub */}
          <svg
            width="1920"
            height="1080"
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
          >
            {SOURCES.map((src, i) => {
              const rad = (src.angle * Math.PI) / 180;
              const tx = CX + RADIUS * Math.cos(rad);
              const ty = CY + RADIUS * Math.sin(rad);
              const lineOpacity = spring({
                frame: frame - (40 + i * 20),
                fps,
                config: SPRING.gentle,
              });
              return (
                <line
                  key={src.name}
                  x1={tx}
                  y1={ty}
                  x2={CX}
                  y2={CY}
                  stroke={COLORS.borderLight}
                  strokeWidth={1.5}
                  strokeDasharray="4 6"
                  opacity={lineOpacity}
                />
              );
            })}
          </svg>

          {/* Central hub */}
          <div
            style={{
              position: "absolute",
              left: CX - 60,
              top: CY - 60,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: `scale(${hubScale})`,
              boxShadow: `0 0 40px ${COLORS.accentBlue}60, 0 0 80px ${COLORS.accentPurple}30`,
            }}
          >
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 18,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: 2,
              }}
            >
              EAKC
            </span>
          </div>
        </div>


        {/* Phase B: Stats card */}
        {frame >= 450 && (
          <div
            style={{
              position: "absolute",
              left: 460,
              top: 350 + statsCardY,
              width: 1000,
              background: COLORS.bgCard,
              border: `1px solid ${COLORS.borderLight}`,
              borderRadius: 16,
              padding: 30,
              opacity: statsCardOpacity,
            }}
          >
            {STATS_ROWS.map((row, i) => {
              const rowOpacity = interpolate(
                frame,
                [470 + i * 10, 500 + i * 10],
                [0, 1],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
              );
              const rowX = interpolate(
                frame,
                [470 + i * 10, 500 + i * 10],
                [20, 0],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
              );
              return (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom:
                      i < STATS_ROWS.length - 1
                        ? `1px solid ${COLORS.border}`
                        : "none",
                    opacity: rowOpacity,
                    transform: `translateX(${rowX}px)`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 18,
                      color: COLORS.textSecondary,
                    }}
                  >
                    {row.label}
                  </span>
                  <span
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 18,
                      fontWeight: 700,
                      color: COLORS.accentTeal,
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              );
            })}
          </div>
        )}

      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
